import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi, sessionTypeApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';
import '../styles/admin-dashboard.css';

const POPULAR_SESSION_ICONS = ['🛋️', '💻', '🏥', '📞', '🎧', '👥', '🧠', '🌿', '🩺', '🤝', '🌟', '🧘', '💬', '📱'];

const DURATION_PRESETS = [30, 45, 50, 60, 90];

const MODE_TITLES = {
  'in-person': 'In-Person (Clinic)',
  'video': 'Video Consultation',
  'call': 'Audio Call',
};

function AdminSessionTypesPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modeFilter, setModeFilter] = useState('all'); // 'all', 'in-person', 'video', 'call'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    sessionMode: 'video',
    description: '',
    icon: '💻',
    defaultDuration: 50,
    requiresLocation: false,
    displayOrder: 0,
    isActive: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, sessionType: null, loading: false });

  // Notification Toast
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchSessionTypes = useCallback(async () => {
    try {
      setLoading(true);
      let list = [];
      try {
        const res = await adminApi.getSessionTypes({ all: 'true' });
        if (Array.isArray(res?.sessionTypes)) list = res.sessionTypes;
        else if (Array.isArray(res?.data)) list = res.data;
        else if (Array.isArray(res)) list = res;
      } catch (adminErr) {
        console.warn('adminApi.getSessionTypes error, trying sessionTypeApi:', adminErr);
        try {
          const publicRes = await sessionTypeApi.getPublicSessionTypes({ all: 'true' });
          if (Array.isArray(publicRes?.sessionTypes)) list = publicRes.sessionTypes;
          else if (Array.isArray(publicRes?.data)) list = publicRes.data;
          else if (Array.isArray(publicRes)) list = publicRes;
        } catch (pubErr) {
          console.warn('sessionTypeApi error, trying direct fetch:', pubErr);
          const directRes = await fetch('/api/session-types?all=true');
          const directData = await directRes.json();
          if (Array.isArray(directData?.sessionTypes)) list = directData.sessionTypes;
        }
      }

      setSessionTypes(list);
    } catch (err) {
      console.error('Error fetching session types:', err);
      showFeedback('error', err.message || 'Failed to load session types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessionTypes();
  }, [fetchSessionTypes]);

  // Statistics
  const totalCount = sessionTypes.length;
  const inPersonCount = sessionTypes.filter((s) => s.sessionMode === 'in-person').length;
  const videoCount = sessionTypes.filter((s) => s.sessionMode === 'video').length;
  const callCount = sessionTypes.filter((s) => s.sessionMode === 'call').length;
  const activeCount = sessionTypes.filter((s) => s.isActive !== false).length;

  // Filtered List
  const filteredSessionTypes = sessionTypes.filter((item) => {
    const q = searchQuery.trim().toLowerCase();
    const modeName = (MODE_TITLES[item.sessionMode] || item.sessionMode || '').toLowerCase();
    const matchesSearch =
      !q ||
      modeName.includes(q) ||
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.description && item.description.toLowerCase().includes(q));

    const matchesMode =
      modeFilter === 'all' || item.sessionMode === modeFilter;

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && item.isActive !== false) ||
      (statusFilter === 'inactive' && item.isActive === false);

    return matchesSearch && matchesMode && matchesStatus;
  });

  const totalPages = Math.ceil(filteredSessionTypes.length / itemsPerPage) || 1;
  const paginatedSessionTypes = filteredSessionTypes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Modal Open Handlers
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      sessionMode: 'video',
      description: '',
      icon: '💻',
      defaultDuration: 50,
      requiresLocation: false,
      displayOrder: sessionTypes.length + 1,
      isActive: true,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setModalMode('edit');
    setEditingId(item._id);
    setFormData({
      sessionMode: item.sessionMode || 'video',
      description: item.description || '',
      icon: item.icon || (item.sessionMode === 'in-person' ? '🏥' : item.sessionMode === 'video' ? '💻' : '📞'),
      defaultDuration: item.defaultDuration || 50,
      requiresLocation: Boolean(item.requiresLocation),
      displayOrder: item.displayOrder || 0,
      isActive: item.isActive !== false,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      setFormLoading(true);
      setFormError('');

      const cleanName = MODE_TITLES[formData.sessionMode] || 'Session Type';
      const cleanSlug = formData.sessionMode;

      const payload = {
        name: cleanName,
        slug: cleanSlug,
        sessionMode: formData.sessionMode,
        description: formData.description.trim(),
        icon: formData.icon.trim() || (formData.sessionMode === 'in-person' ? '🏥' : formData.sessionMode === 'video' ? '💻' : '📞'),
        defaultDuration: parseInt(formData.defaultDuration, 10) || 50,
        requiresLocation: formData.sessionMode === 'in-person',
        displayOrder: parseInt(formData.displayOrder, 10) || 0,
        isActive: Boolean(formData.isActive),
      };

      if (modalMode === 'create') {
        const res = await adminApi.createSessionType(payload);
        const newObj = res.sessionType || res;
        setSessionTypes((prev) => [...prev, newObj]);
        showFeedback('success', `Session Type "${cleanName}" created successfully!`);
      } else {
        const res = await adminApi.updateSessionType(editingId, payload);
        const updatedObj = res.sessionType || res;
        setSessionTypes((prev) =>
          prev.map((s) => (s._id === editingId ? { ...s, ...updatedObj } : s))
        );
        showFeedback('success', `Session Type "${cleanName}" updated successfully!`);
      }

      setIsModalOpen(false);
      fetchSessionTypes();
    } catch (err) {
      console.error('Save session type error:', err);
      setFormError(err.message || 'Failed to save session type');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const newStatus = !item.isActive;
      const res = await adminApi.updateSessionType(item._id, { isActive: newStatus });
      const updated = res.sessionType || res;
      setSessionTypes((prev) =>
        prev.map((s) => (s._id === item._id ? { ...s, ...updated, isActive: newStatus } : s))
      );
      showFeedback('success', `Session type "${item.name}" is now ${newStatus ? 'Active' : 'Inactive'}.`);
    } catch (err) {
      console.error('Toggle status error:', err);
      showFeedback('error', err.message || 'Failed to toggle status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.sessionType) return;
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      await adminApi.deleteSessionType(deleteModal.sessionType._id);
      setSessionTypes((prev) => prev.filter((s) => s._id !== deleteModal.sessionType._id));
      showFeedback('success', `Session Type "${deleteModal.sessionType.name}" deleted successfully.`);
      setDeleteModal({ isOpen: false, sessionType: null, loading: false });
    } catch (err) {
      console.error('Delete error:', err);
      showFeedback('error', err.message || 'Failed to delete session type');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const getModeBadge = (mode) => {
    switch (mode) {
      case 'in-person':
        return { label: '🏥 In-Person (Clinic)', bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' };
      case 'video':
        return { label: '💻 Video Consultation', bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' };
      case 'call':
        return { label: '📞 Audio Call', bg: '#faf5ff', text: '#6b21a8', border: '#e9d5ff' };
      default:
        return { label: mode, bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <DashboardSidebar userRole="admin" />

      <main className="admin-main-wrapper admin-custom-scrollbar">
        <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '32px 36px 80px 36px' }}>
          
          {/* Breadcrumb Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b', marginBottom: '16px', fontWeight: 500 }}>
            <span>Admin</span>
            <span>/</span>
            <span>Clinical Master</span>
            <span>/</span>
            <span style={{ color: '#7c3aed', fontWeight: 600 }}>Session Types & Consultation Modes</span>
          </div>

          {/* Top Header Banner */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)',
              borderRadius: '20px',
              padding: '32px 36px',
              color: '#ffffff',
              marginBottom: '28px',
              boxShadow: '0 12px 32px rgba(49, 46, 129, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '20px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background Glow */}
            <div
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-40px',
                width: '180px',
                height: '180px',
                background: 'radial-gradient(circle, rgba(124, 58, 237, 0.4) 0%, rgba(255,255,255,0) 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
              }}
            />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
                <span
                  style={{
                    fontSize: '28px',
                    width: '54px',
                    height: '54px',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  🛋️
                </span>
                <div>
                  <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800, letterSpacing: '-0.5px', color: '#ffffff' }}>
                    Session Types & Consultation Modes
                  </h1>
                  <span style={{ fontSize: '12px', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Practitioner Consultation Services
                  </span>
                </div>
              </div>
              <p style={{ margin: '8px 0 0 0', color: 'rgba(255, 255, 255, 0.85)', fontSize: '14.5px', maxWidth: '640px', lineHeight: 1.5 }}>
                Configure and manage consultation formats: <strong>In-Person (Clinic)</strong>, <strong>Video Consultation</strong>, and <strong>Audio Call</strong> with duration and clinic location policies.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', position: 'relative', zIndex: 1 }}>
              <button
                type="button"
                onClick={handleOpenCreateModal}
                style={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '13px 24px',
                  fontWeight: 700,
                  fontSize: '14.5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 6px 20px rgba(168, 85, 247, 0.35)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 85, 247, 0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(168, 85, 247, 0.35)';
                }}
              >
                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>➕</span> Add New Session Type
              </button>
            </div>
          </div>

          {/* Toast Notification */}
          {feedback.message && (
            <div
              style={{
                position: 'fixed',
                top: '24px',
                right: '24px',
                zIndex: 99999,
                padding: '14px 22px',
                borderRadius: '14px',
                fontWeight: 700,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
                background: feedback.type === 'error' ? '#991b1b' : '#065f46',
                color: '#ffffff',
                border: feedback.type === 'error' ? '1px solid #ef4444' : '1px solid #10b981',
                animation: 'adminSlideDown 0.2s ease-out',
              }}
            >
              <span style={{ fontSize: '18px' }}>{feedback.type === 'error' ? '⚠️' : '✅'}</span>
              <span>{feedback.message}</span>
              <button
                onClick={() => setFeedback({ type: '', message: '' })}
                style={{ background: 'none', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: '16px', padding: 0, marginLeft: '8px' }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Quick Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '28px' }}>
            {/* Metric 1: Total */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#f1f5f9',
                  color: '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                🛋️
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Modes
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>
                  {totalCount}
                </div>
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '2px', fontWeight: 600 }}>
                  {activeCount} Active on Platform
                </div>
              </div>
            </div>

            {/* Metric 2: Video */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#eff6ff',
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                💻
              </div>
              <div>
                <div style={{ color: '#1e40af', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Video Consultations
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#2563eb', marginTop: '2px' }}>
                  {videoCount}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Online video room
                </div>
              </div>
            </div>

            {/* Metric 3: In-Person */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#ecfdf5',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                🏥
              </div>
              <div>
                <div style={{ color: '#065f46', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Clinic Visits
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#059669', marginTop: '2px' }}>
                  {inPersonCount}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Requires clinic venue
                </div>
              </div>
            </div>

            {/* Metric 4: Audio Call */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#f5f3ff',
                  color: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                📞
              </div>
              <div>
                <div style={{ color: '#6b21a8', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Audio Call
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#7c3aed', marginTop: '2px' }}>
                  {callCount}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Voice phone consultations
                </div>
              </div>
            </div>
          </div>

          {/* Filter Toolbar */}
          <div
            style={{
              background: '#ffffff',
              padding: '18px 24px',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
              marginBottom: '24px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Search Input */}
            <div style={{ position: 'relative', flex: '1', minWidth: '280px', maxWidth: '440px' }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '16px' }}>
                🔍
              </span>
              <input
                type="text"
                placeholder="Search session modes, names, details..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '11px 16px 11px 40px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#f8fafc',
                  color: '#1e293b',
                  boxSizing: 'border-box',
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Mode Filter Pills */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { key: 'all', label: '🌟 All Modes' },
                { key: 'in-person', label: '🏥 In-Person' },
                { key: 'video', label: '💻 Video' },
                { key: 'call', label: '📞 Call' },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => {
                    setModeFilter(m.key);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '10px',
                    border: modeFilter === m.key ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                    background: modeFilter === m.key ? '#f5f3ff' : '#ffffff',
                    color: modeFilter === m.key ? '#7c3aed' : '#475569',
                    fontWeight: modeFilter === m.key ? 700 : 500,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '9px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '13.5px',
                  background: '#ffffff',
                  outline: 'none',
                  cursor: 'pointer',
                  color: '#334155',
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', padding: '0 4px' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Showing <strong>{filteredSessionTypes.length}</strong> of <strong>{sessionTypes.length}</strong> consultation types
            </span>
          </div>

          {/* Data Table Card */}
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            {loading ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                <div style={{ fontWeight: 600, fontSize: '15px' }}>Loading session types...</div>
              </div>
            ) : filteredSessionTypes.length === 0 ? (
              <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
                <div style={{ fontSize: '48px', marginBottom: '14px' }}>🛋️</div>
                <h3 style={{ margin: '0 0 6px 0', color: '#0f172a', fontWeight: 800, fontSize: '18px' }}>
                  No Session Types Found
                </h3>
                <p style={{ margin: '0 auto 20px auto', fontSize: '14px', maxWidth: '420px' }}>
                  {searchQuery || modeFilter !== 'all'
                    ? 'Try adjusting your filters or search query.'
                    : 'Create your first session type to get started.'}
                </p>
                <button
                  type="button"
                  onClick={handleOpenCreateModal}
                  style={{
                    padding: '11px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(124, 58, 237, 0.25)',
                  }}
                >
                  ➕ Add Session Type
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <th style={{ padding: '16px 22px' }}>Session Type</th>
                      <th style={{ padding: '16px 20px' }}>Mode</th>
                      <th style={{ padding: '16px 20px' }}>Default Duration</th>
                      <th style={{ padding: '16px 20px' }}>Location Policy</th>
                      <th style={{ padding: '16px 20px', textAlign: 'center' }}>Display Order</th>
                      <th style={{ padding: '16px 20px', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '16px 22px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSessionTypes.map((item) => {
                      const badge = getModeBadge(item.sessionMode);
                      const isItemActive = item.isActive !== false;
                      const requiresLoc = Boolean(item.requiresLocation || item.sessionMode === 'in-person');

                      return (
                        <tr
                          key={item._id}
                          style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.15s ease' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fbfcfe')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          {/* Name & Icon */}
                          <td style={{ padding: '16px 22px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <span
                                style={{
                                  fontSize: '22px',
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '12px',
                                  background: badge.bg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: `1.5px solid ${badge.border}`,
                                  flexShrink: 0,
                                }}
                              >
                                {item.icon || '🛋️'}
                              </span>
                              <div>
                                <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14.5px' }}>
                                  {item.name || MODE_TITLES[item.sessionMode] || item.sessionMode}
                                </div>
                                {item.description && (
                                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {item.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Mode Badge */}
                          <td style={{ padding: '16px 20px' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '5px 12px',
                                borderRadius: '20px',
                                background: badge.bg,
                                color: badge.text,
                                border: `1px solid ${badge.border}`,
                                fontWeight: 700,
                                fontSize: '12.5px',
                              }}
                            >
                              {badge.label}
                            </span>
                          </td>

                          {/* Duration */}
                          <td style={{ padding: '16px 20px' }}>
                            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px', background: '#f1f5f9', padding: '4px 10px', borderRadius: '8px' }}>
                              ⏱️ {item.defaultDuration || 50} mins
                            </span>
                          </td>

                          {/* Location Req */}
                          <td style={{ padding: '16px 20px' }}>
                            {requiresLoc ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#065f46', background: '#ecfdf5', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: '1px solid #a7f3d0' }}>
                                📍 Clinic Address Required
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#64748b', background: '#f8fafc', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', border: '1px solid #e2e8f0' }}>
                                🌐 Online / Virtual
                              </span>
                            )}
                          </td>

                          {/* Order */}
                          <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                            <span style={{ fontWeight: 700, color: '#334155', fontSize: '13px', background: '#f1f5f9', padding: '4px 10px', borderRadius: '8px' }}>
                              {item.displayOrder || 0}
                            </span>
                          </td>

                          {/* Status Toggle */}
                          <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleToggleActive(item)}
                              title="Click to toggle session type status"
                              style={{
                                padding: '6px 14px',
                                borderRadius: '20px',
                                border: isItemActive ? '1px solid #a7f3d0' : '1px solid #cbd5e1',
                                background: isItemActive ? '#ecfdf5' : '#f1f5f9',
                                color: isItemActive ? '#065f46' : '#64748b',
                                fontWeight: 700,
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <span
                                style={{
                                  width: '7px',
                                  height: '7px',
                                  borderRadius: '50%',
                                  background: isItemActive ? '#10b981' : '#94a3b8',
                                }}
                              />
                              {isItemActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '16px 22px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => handleOpenEditModal(item)}
                                title="Edit Session Type"
                                style={{
                                  padding: '7px 14px',
                                  borderRadius: '8px',
                                  border: '1px solid #cbd5e1',
                                  background: '#ffffff',
                                  color: '#334155',
                                  fontWeight: 600,
                                  fontSize: '12.5px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#7c3aed';
                                  e.currentTarget.style.color = '#7c3aed';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                  e.currentTarget.style.color = '#334155';
                                }}
                              >
                                <span>✏️</span> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteModal({ isOpen: true, sessionType: item, loading: false })}
                                title="Delete Session Type"
                                style={{
                                  padding: '7px 12px',
                                  borderRadius: '8px',
                                  border: '1px solid #fecaca',
                                  background: '#fff5f5',
                                  color: '#dc2626',
                                  fontWeight: 600,
                                  fontSize: '12.5px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fff5f5';
                                }}
                              >
                                <span>🗑️</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredSessionTypes.length > 0 && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9' }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={filteredSessionTypes.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(sz) => {
                    setItemsPerPage(sz);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ========================================================================= */}
      {/* CREATE / EDIT MODAL */}
      {/* ========================================================================= */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            animation: 'adminFadeIn 0.2s ease-out',
          }}
          onClick={() => !formLoading && setIsModalOpen(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '620px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              animation: 'adminScaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {modalMode === 'create' ? '➕ Add Session Mode' : `✏️ Edit ${MODE_TITLES[formData.sessionMode] || 'Session Type'}`}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure consultation delivery mode, duration, and clinical requirements.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: '50%', width: '32px', height: '32px', fontSize: '1rem', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            {formError && (
              <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '13px', marginBottom: '18px', fontWeight: 600 }}>
                ⚠️ {formError}
              </div>
            )}

            <form onSubmit={handleFormSubmit}>
              {/* Session Mode Selector */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>
                  Select Session Mode <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  {[
                    { mode: 'in-person', icon: '🏥', label: 'In-Person', sub: 'Clinic / Offline' },
                    { mode: 'video', icon: '💻', label: 'Video', sub: 'Online Encrypted' },
                    { mode: 'call', icon: '📞', label: 'Audio Call', sub: 'Voice / Phone' },
                  ].map((m) => {
                    const isSelected = formData.sessionMode === m.mode;
                    return (
                      <button
                        key={m.mode}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            sessionMode: m.mode,
                            icon: prev.icon || m.icon,
                            requiresLocation: m.mode === 'in-person',
                          }));
                        }}
                        style={{
                          padding: '14px 10px',
                          borderRadius: '12px',
                          border: isSelected ? '2px solid #7c3aed' : '1.5px solid #cbd5e1',
                          background: isSelected ? '#f5f3ff' : '#ffffff',
                          color: isSelected ? '#7c3aed' : '#475569',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.15s ease',
                          boxShadow: isSelected ? '0 4px 12px rgba(124, 58, 237, 0.15)' : 'none',
                        }}
                      >
                        <div style={{ fontSize: '24px', marginBottom: '4px' }}>{m.icon}</div>
                        <div style={{ fontWeight: 800, fontSize: '13.5px' }}>{m.label}</div>
                        <div style={{ fontSize: '11px', color: isSelected ? '#6d28d9' : '#94a3b8', marginTop: '2px' }}>{m.sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Icon Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Icon / Emoji
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    style={{ width: '56px', padding: '8px', textAlign: 'center', fontSize: '20px', borderRadius: '8px', border: '1.5px solid #cbd5e1', outline: 'none' }}
                  />
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {POPULAR_SESSION_ICONS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: emoji })}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          border: formData.icon === emoji ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                          background: formData.icon === emoji ? '#f5f3ff' : '#ffffff',
                          fontSize: '18px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Duration Presets */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Default Duration (Minutes)
                </label>
                <input
                  type="number"
                  min="5"
                  max="480"
                  value={formData.defaultDuration}
                  onChange={(e) => setFormData({ ...formData, defaultDuration: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  {DURATION_PRESETS.map((dur) => (
                    <button
                      key={dur}
                      type="button"
                      onClick={() => setFormData({ ...formData, defaultDuration: dur })}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '8px',
                        border: parseInt(formData.defaultDuration, 10) === dur ? '1.5px solid #7c3aed' : '1px solid #e2e8f0',
                        background: parseInt(formData.defaultDuration, 10) === dur ? '#f5f3ff' : '#ffffff',
                        color: parseInt(formData.defaultDuration, 10) === dur ? '#7c3aed' : '#64748b',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {dur} Mins
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Description & Instructions
                </label>
                <textarea
                  rows="3"
                  placeholder="Provide an overview of this session mode (preparation instructions, equipment required, etc.)..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #cbd5e1', fontSize: '13.5px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              {/* Settings & Active Status */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '22px', background: '#f8fafc', padding: '14px 16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                    Display Sort Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                    style={{ width: '100px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '13.5px', color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Active on Platform</span>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={formLoading}
                  style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(124, 58, 237, 0.3)',
                  }}
                >
                  {formLoading ? 'Saving...' : modalMode === 'create' ? 'Create Session Type' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ========================================================================= */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            animation: 'adminFadeIn 0.2s ease-out',
          }}
          onClick={() => !deleteModal.loading && setDeleteModal({ isOpen: false, sessionType: null, loading: false })}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '460px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              animation: 'adminScaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
              Delete Session Type?
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
              Are you sure you want to permanently delete{' '}
              <strong style={{ color: '#0f172a' }}>"{deleteModal.sessionType?.name}"</strong>?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, sessionType: null, loading: false })}
                disabled={deleteModal.loading}
                style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteModal.loading}
                style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#ef4444', color: '#ffffff', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(239, 68, 68, 0.35)' }}
              >
                {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSessionTypesPage;
